import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useToast } from '../components/contexts/ToastContext';
import apiService from '../services/apiService';
import { downloadFile } from '../utils/downloadHelpers';
import { getTiposNotaOptions } from '../utils/cotizacionHelpers';
import useConfirmAction from './useConfirmAction';
import { arrayMove } from '@dnd-kit/sortable';

/**
 * Hook para gestionar el estado y operaciones de la vista detalle de cotización.
 *
 * @param {number|string} id - ID de la cotización
 * @returns {Object} Estado y handlers de la cotización
 */
function useCotizacionDetalle(id) {
  const navigate = useNavigate();
  const { showToast } = useToast();

  const [cotizacion, setCotizacion] = useState(null);
  const [items, setItems] = useState([]);
  const [revisiones, setRevisiones] = useState([]);
  const [equipos, setEquipos] = useState([]);
  const [equiposLoading, setEquiposLoading] = useState(false);
  const [loading, setLoading] = useState(true);

  const [itemModalOpen, setItemModalOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const confirmItem = useConfirmAction();

  const [isReordering, setIsReordering] = useState(false);

  const [notas, setNotas] = useState([]);
  const [notasLoading, setNotasLoading] = useState(false);
  const [notaModalOpen, setNotaModalOpen] = useState(false);
  const [editingNota, setEditingNota] = useState(null);
  const confirmNota = useConfirmAction();
  const confirmEmail = useConfirmAction();

  const itemsRef = useRef(items);
  useEffect(() => {
    itemsRef.current = items;
  }, [items]);

  const [pdfLoading, setPdfLoading] = useState(false);
  const [emailLoading, setEmailLoading] = useState(false);

  const [showDescuentoForm, setShowDescuentoForm] = useState(false);
  const [descuentoTipo, setDescuentoTipo] = useState('porcentaje');
  const [descuentoValor, setDescuentoValor] = useState('');
  const [savingDescuento, setSavingDescuento] = useState(false);

  const fetchCotizacion = useCallback(async () => {
    try {
      const res = await apiService.getCotizacion(id);
      if (res.success) setCotizacion(res.data);
    } catch {
      showToast('Error al cargar la cotización', 'error');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]); // showToast omitido intencionalmente (estable, evita recreación)

  const fetchItems = useCallback(async () => {
    if (!id) return;
    try {
      const res = await apiService.getCotizacionItems(id);
      if (res.success) setItems(res.data || []);
    } catch {
      showToast('Error al cargar items', 'error');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]); // showToast omitido intencionalmente (estable, evita recreación)

  const fetchRevisiones = useCallback(async () => {
    try {
      const res = await apiService.getRevisionesCotizacion(id);
      if (res.success) setRevisiones(res.data);
    } catch {
      showToast('Error al cargar revisiones', 'error');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]); // showToast omitido (estable)

  const fetchNotas = useCallback(async () => {
    if (!id) return;
    setNotasLoading(true);
    try {
      const res = await apiService.getCotizacionNotas(id);
      if (res.success) setNotas(res.data || []);
    } catch {
      showToast('Error al cargar notas', 'error');
    } finally {
      setNotasLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]); // showToast omitido intencionalmente (estable, evita recreación)

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await Promise.all([
        fetchCotizacion(),
        fetchItems(),
        fetchRevisiones(),
        fetchNotas(),
        (async () => {
          setEquiposLoading(true);
          try {
            const res = await apiService.getEquipos({ limit: 1000 });
            if (res.success) setEquipos(res.data);
          } catch {
            showToast('Error al cargar equipos', 'error');
          } finally {
            setEquiposLoading(false);
          }
        })()
      ]);
      setLoading(false);
    };
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchCotizacion, fetchItems, fetchRevisiones, fetchNotas]); // showToast omitido: estable (patrón del proyecto)

  const isEditable = useMemo(() => cotizacion?.estado === 'borrador', [cotizacion]);

  const canCreateRevision = useMemo(
    () => cotizacion && ['enviada', 'rechazada'].includes(cotizacion.estado),
    [cotizacion]
  );

  const handleCreateItem = () => {
    if (!isEditable) {
      showToast('No se pueden agregar items a una cotización aprobada o rechazada', 'warning');
      return;
    }
    setEditingItem(null);
    setItemModalOpen(true);
  };

  const handleEditItem = (item) => {
    if (!isEditable) {
      showToast('No se pueden editar items de una cotización aprobada o rechazada', 'warning');
      return;
    }
    setEditingItem(item);
    setItemModalOpen(true);
  };

  const closeItemModal = () => {
    setItemModalOpen(false);
    setEditingItem(null);
  };

  const handleSaveItem = useCallback(async (data) => {
    if (!isEditable) {
      showToast('No se pueden modificar items de una cotización aprobada o rechazada', 'warning');
      return;
    }
    try {
      let response;
      if (editingItem) {
        response = await apiService.updateCotizacionItem(id, editingItem.id, data);
      } else {
        response = await apiService.createCotizacionItem(id, data);
      }
      if (response.success) {
        showToast('Item guardado exitosamente', 'success');
        closeItemModal();
        await Promise.all([fetchItems(), fetchCotizacion()]);
      } else {
        showToast(response.message || 'Error al guardar item', 'error');
      }
    } catch {
      showToast('Error al guardar item', 'error');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editingItem, id, isEditable, fetchItems, fetchCotizacion]); // showToast omitido (estable)

  const handleDeleteItem = (item) => {
    confirmItem.open(item);
  };

  const confirmDeleteItem = useCallback(async () => {
    const item = confirmItem.state.item;
    if (!item) return;
    try {
      const res = await apiService.deleteCotizacionItem(id, item.id);
      if (res.success) {
        showToast('Item eliminado exitosamente', 'success');
        await Promise.all([fetchItems(), fetchCotizacion()]);
      }
    } catch {
      showToast('Error al eliminar item', 'error');
    } finally {
      confirmItem.reset();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, confirmItem.state.item?.id, fetchItems, fetchCotizacion]); // showToast, confirmItem.reset omitidos (estables)

  const handleOpenDescuento = () => {
    if (cotizacion.descuento_porcentaje > 0) {
      setDescuentoTipo('porcentaje');
      setDescuentoValor(String(cotizacion.descuento_porcentaje));
    } else if (cotizacion.descuento_monto > 0) {
      setDescuentoTipo('monto');
      setDescuentoValor(String(cotizacion.descuento_monto));
    } else {
      setDescuentoTipo('porcentaje');
      setDescuentoValor('');
    }
    setShowDescuentoForm(true);
  };

  const handleSaveDescuento = useCallback(async () => {
    const valor = parseFloat(descuentoValor) || 0;
    const data = descuentoTipo === 'porcentaje'
      ? { descuento_porcentaje: valor, descuento_monto: 0 }
      : { descuento_porcentaje: 0, descuento_monto: valor };

    setSavingDescuento(true);
    try {
      const res = await apiService.updateCotizacion(id, data);
      if (res.success) {
        showToast('Descuento actualizado', 'success');
        setShowDescuentoForm(false);
        setDescuentoValor('');
        await fetchCotizacion();
      } else {
        showToast(res.message || 'Error al guardar descuento', 'error');
      }
    } catch {
      showToast('Error al guardar descuento', 'error');
    } finally {
      setSavingDescuento(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, descuentoTipo, descuentoValor, fetchCotizacion]); // showToast omitido (estable)

  const handleQuitarDescuento = useCallback(async () => {
    setSavingDescuento(true);
    try {
      const res = await apiService.updateCotizacion(id, { descuento_porcentaje: 0, descuento_monto: 0 });
      if (res.success) {
        showToast('Descuento eliminado', 'success');
        await fetchCotizacion();
      }
    } catch {
      showToast('Error al eliminar descuento', 'error');
    } finally {
      setSavingDescuento(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, fetchCotizacion]); // showToast omitido (estable)

  const cancelDescuento = () => {
    setShowDescuentoForm(false);
    setDescuentoValor('');
  };

  const handleDragEnd = useCallback(async (event) => {
    const { active, over } = event;
    if (isReordering) {
      showToast('Espere a que termine la operación anterior', 'warning');
      return;
    }
    if (!over || active.id === over.id) return;

    const currentItems = itemsRef.current;
    const oldIndex = currentItems.findIndex(item => item.id === active.id);
    const newIndex = currentItems.findIndex(item => item.id === over.id);
    if (oldIndex === -1 || newIndex === -1) return;

    const previousItems = [...currentItems];
    const reorderedItems = arrayMove(currentItems, oldIndex, newIndex).map(
      (item, index) => ({ ...item, orden: index + 1 })
    );

    setItems(reorderedItems);
    setIsReordering(true);

    try {
      const ordenArray = reorderedItems.map(item => ({ id: item.id, orden: item.orden }));
      const res = await apiService.reordenarCotizacionItems(id, ordenArray);
      if (res.success) {
        showToast('Orden actualizado exitosamente', 'success');
        await fetchItems();
        await fetchCotizacion();
      } else {
        throw new Error(res.message || 'Error al reordenar');
      }
    } catch (err) {
      setItems(previousItems);
      showToast('Error al actualizar el orden', 'error');
      console.error('Error reordenando items:', err);
    } finally {
      setIsReordering(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, isReordering, fetchItems, fetchCotizacion]); // showToast se omite: estable

  const handleDownloadPdf = useCallback(async () => {
    setPdfLoading(true);
    try {
      const { blob, filename } = await apiService.generarPdfCotizacion(id);
      downloadFile(blob, filename);
      showToast('PDF descargado exitosamente', 'success');
    } catch (error) {
      showToast(error.message || 'Error al generar el PDF', 'error');
    } finally {
      setPdfLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]); // showToast omitido — patrón del proyecto (MEMORY.md)

  const handleEnviarEmail = useCallback(() => {
    confirmEmail.open();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // confirmEmail.open omitido: internamente usa setState (estable)

  const confirmEnviarEmail = useCallback(async () => {
    confirmEmail.reset();
    setEmailLoading(true);
    try {
      const res = await apiService.enviarCotizacionEmail(id);
      showToast(res.message || 'Email enviado exitosamente', 'success');
      fetchCotizacion();
    } catch (error) {
      showToast(error.message || 'Error al enviar el email', 'error');
    } finally {
      setEmailLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]); // showToast, fetchCotizacion, confirmEmail.reset omitidos — patrón del proyecto (MEMORY.md)

  const handleCrearRevision = useCallback(async () => {
    try {
      const res = await apiService.crearRevisionCotizacion(id);
      if (res.success) {
        showToast('Revisión creada exitosamente', 'success');
        navigate(`/cotizaciones/${res.data.id}`);
      }
    } catch (err) {
      showToast(err.message || 'Error al crear revisión', 'error');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, navigate]); // showToast omitido (estable)

  const handleVerRevision = (revisionId) => {
    navigate(`/cotizaciones/${revisionId}`);
  };

  const handleBack = () => {
    navigate('/cotizaciones');
  };

  const handleCreateNota = () => {
    if (!isEditable) {
      showToast('No se pueden agregar notas a una cotización aprobada o rechazada', 'warning');
      return;
    }
    setEditingNota(null);
    setNotaModalOpen(true);
  };

  const handleEditNota = (nota) => {
    if (!isEditable) {
      showToast('No se pueden editar notas de una cotización aprobada o rechazada', 'warning');
      return;
    }
    setEditingNota(nota);
    setNotaModalOpen(true);
  };

  const closeNotaModal = () => {
    setNotaModalOpen(false);
    setEditingNota(null);
  };

  const handleSaveNota = useCallback(async (data) => {
    if (!isEditable) {
      showToast('No se pueden modificar notas de una cotización aprobada o rechazada', 'warning');
      closeNotaModal();
      return;
    }
    try {
      let response;
      if (editingNota) {
        response = await apiService.updateCotizacionNota(id, editingNota.id, data);
      } else {
        response = await apiService.createCotizacionNota(id, data);
      }
      if (response.success) {
        showToast('Nota guardada exitosamente', 'success');
        closeNotaModal();
        await fetchNotas();
      } else {
        showToast(response.message || 'Error al guardar nota', 'error');
      }
    } catch {
      showToast('Error al guardar nota', 'error');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editingNota, id, isEditable, fetchNotas]); // showToast omitido (estable)

  const handleDeleteNota = (nota) => {
    confirmNota.open(nota);
  };

  const confirmDeleteNota = useCallback(async () => {
    const nota = confirmNota.state.item;
    if (!nota) return;
    try {
      const res = await apiService.deleteCotizacionNota(id, nota.id);
      if (res.success) {
        showToast('Nota eliminada exitosamente', 'success');
        await fetchNotas();
      } else {
        showToast(res.message || 'Error al eliminar nota', 'error');
      }
    } catch {
      showToast('Error al eliminar nota', 'error');
    } finally {
      confirmNota.reset();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, confirmNota.state.item?.id, fetchNotas]); // showToast, confirmNota.reset omitidos (estables)

  const handleEquipoSelect = (equipoId, setFieldValue) => {
    if (!equipoId || equipoId === '') {
      setFieldValue('tipo', '');
      setFieldValue('descripcion', '');
      setFieldValue('precio_unitario', '');
      setFieldValue('unidad', '');
      return;
    }
    const equipo = equipos.find(e => e.id === parseInt(equipoId));
    if (equipo) {
      setFieldValue('tipo', 'equipo');
      setFieldValue('descripcion', [equipo.marca, equipo.modelo].filter(Boolean).join(' ') || equipo.codigo);
    }
  };

  const itemModalFields = useMemo(() => [
    {
      name: 'equipo_id', label: 'Equipo (opcional)', type: 'select',
      options: equipos.map(e => ({ value: e.id, label: `${e.codigo} - ${[e.marca, e.modelo].filter(Boolean).join(' ')}` })),
      disabled: equiposLoading,
      onChange: (value, setFieldValue) => handleEquipoSelect(value, setFieldValue)
    },
    {
      name: 'tipo', label: 'Tipo', type: 'select', required: true,
      hidden: (formData) => !!formData.equipo_id,
      options: [
        { value: 'equipo', label: 'Equipo' }, { value: 'servicio', label: 'Servicio' },
        { value: 'traslado', label: 'Traslado' }, { value: 'combustible', label: 'Combustible' },
        { value: 'operador', label: 'Operador' }, { value: 'otro', label: 'Otro' }
      ]
    },
    { name: 'descripcion', label: 'Descripción', type: 'textarea', required: true, placeholder: 'Descripción detallada del item' },
    { name: 'cantidad', label: 'Cantidad', type: 'number', required: true, min: 0.01, step: 0.01 },
    {
      name: 'unidad', label: 'Unidad', type: 'select', required: true,
      options: [
        { value: 'hora', label: 'Hora' }, { value: 'día', label: 'Día' },
        { value: 'semana', label: 'Semana' }, { value: 'mes', label: 'Mes' },
        { value: 'viaje', label: 'Viaje' }, { value: 'servicio', label: 'Servicio' },
        { value: 'unidad', label: 'Unidad' }
      ]
    },
    { name: 'precio_unitario', label: 'Precio Unitario', type: 'number', required: true, min: 0, step: 0.01 },
    { name: 'codigo', label: 'Código (opcional)', type: 'text', maxLength: 100 }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  ], [equipos, equiposLoading]); // handleEquipoSelect omitido: se recrea con equipos (ya en deps)

  const notaModalFields = useMemo(() => [
    { name: 'tipo', label: 'Tipo', type: 'select', required: true, options: getTiposNotaOptions() },
    { name: 'contenido', label: 'Nota', type: 'textarea', required: true, placeholder: 'Ej: Servicio no considera combustible...' }
  ], []);

  const subtotal = parseFloat(cotizacion?.subtotal || 0);
  const total = parseFloat(cotizacion?.total || 0);
  const descuentoAplicado = subtotal - total;

  return {
    cotizacion, items, revisiones, notas,
    loading, notasLoading,
    itemModalOpen, editingItem, notaModalOpen, editingNota,
    confirmItem, confirmNota, confirmEmail,
    isReordering, pdfLoading, emailLoading,
    showDescuentoForm, descuentoTipo, descuentoValor, savingDescuento,
    subtotal, total, descuentoAplicado,
    itemModalFields, notaModalFields,
    isEditable, canCreateRevision,
    handleCreateItem, handleEditItem, handleSaveItem, handleDeleteItem, confirmDeleteItem, closeItemModal,
    handleOpenDescuento, handleSaveDescuento, handleQuitarDescuento,
    cancelDescuento, setDescuentoTipo, setDescuentoValor,
    handleDragEnd,
    handleDownloadPdf, handleEnviarEmail, confirmEnviarEmail,
    handleBack, handleCrearRevision, handleVerRevision,
    handleCreateNota, handleEditNota, handleSaveNota, handleDeleteNota, confirmDeleteNota,
    closeNotaModal
  };
}

export default useCotizacionDetalle;
